import { Injectable } from '@angular/core';
import { IM365AppDefinition } from '@office-start/app-host-types';
import { m365AppDefinitionsBehaviorSubject } from '@office-start/m365-apps-service';

import { BehaviorSubject, Observable } from 'rxjs';

import { ConfigParserService } from '../shared/services/config-parser.service';
import { CapabilitiesService } from '../shared/shared-context/capabilities.service';
import { M356AppsSideLoadingService } from './m365-apps-sideloading.service';

import { getAppManifests } from './m365-preinstalled-apps';

export interface M356AppsConfig {
    firstPartyAppIds: Array<string>;
    thirdPartyAppIds: Array<string>;
}

/**
 * M365AppDefinitions as provided in the page
 */
interface M365AppDefinitions {
    appDefinitions: Array<IM365AppDefinition>;
}

@Injectable({
    providedIn: 'root'
})
export class M356AppsConfigService {
    public readonly config: M356AppsConfig;
    public readonly appDefinitions : Observable<ReadonlyArray<IM365AppDefinition>>;
    private readonly _appDefinitions : BehaviorSubject<ReadonlyArray<IM365AppDefinition>>;

    constructor(
            private readonly configParserService: ConfigParserService,
            private readonly sideLoadingService: M356AppsSideLoadingService,
            private readonly capabilitiesService: CapabilitiesService

        ) {
        this.config = this.configParserService.parse<M356AppsConfig>('m365AppsConfig') || {
            firstPartyAppIds: [],
            thirdPartyAppIds: []
        };

        this._appDefinitions = m365AppDefinitionsBehaviorSubject;
        this.appDefinitions = this._appDefinitions.asObservable();
        this.loadAppDefinitions();

        this.sideLoadingService.m365SideLoadedAppsSource.subscribe(_sideloadedApps => {
            this.loadAppDefinitions();
        });
    }

    public getPinnedApps(): ReadonlyArray<string> {
        return Array.from(getAppManifests(this.capabilitiesService.capabilities).keys());
    }

    public getPinnedAppDefinitions(): ReadonlyArray<IM365AppDefinition> {
        return this._appDefinitions.value;
    }

    public getAppManifest(appName: string): Promise<IM365AppDefinition> {
        const appDefinition = this._appDefinitions.value.find(currentApp => currentApp.name.toLowerCase() === appName.toLowerCase());

        if (appDefinition) {
            return Promise.resolve(appDefinition);
        } else {
            return Promise.reject();
        }
    }

    public getAppManifestById(id: string): Promise<IM365AppDefinition> {
        const appDefinition = this._appDefinitions.value.find(currentApp => currentApp.id === id);

        if (appDefinition) {
            return Promise.resolve(appDefinition);
        } else {
            return Promise.reject();
        }
    }

    private loadAppDefinitions(): void {
        // App definitions retrieved from App Service is serialized and put under domElement with id='m365AppsData'
        const appsFromAppServices = this.configParserService.parse<M365AppDefinitions>('m365AppsData') || {
            appDefinitions: []
        };

        // App Service returns app definition with null value - until  they fix the bug, remove those app definitions
        const installedAppDefinitions = appsFromAppServices.appDefinitions.filter(appDefinition => appDefinition);

        const updatedAppDefinitions: ReadonlyArray<IM365AppDefinition> = [
            ...getAppManifests(this.capabilitiesService.capabilities).values(),
            ...installedAppDefinitions,
            ...this.sideLoadingService.m365SideLoadedApps
        ];

        this._appDefinitions.next(updatedAppDefinitions);
    }

}
